import { useEffect, useMemo, useState } from "react";
import { formatMoney } from "../../lib/format-fr.js";
import { trpc } from "../../lib/trpc.js";
import { useT } from "../../i18n/index.js";

/**
 * F-pay-allocation (CEO 2026-09-27, CHIR-AUTO F2026-00035) — pagamento PARCIAL
 * de fatura multi-carro: o operador marca quais carros o dinheiro quita; o
 * Gestion sugere os mais antigos (nº de ordem) até o valor disponível. Carro
 * quitado → repasse ao técnico liberado; a sobra fica como crédito na fatura.
 *
 * Dois usos:
 *  - conciliação (`depositHt` + `onConfirm`): escolhe os carros ANTES de ligar
 *    o depósito — quem chama grava o recibo junto com a distribuição;
 *  - fatura (sem `onConfirm`): altera a distribuição já feita e salva aqui.
 */
export function PaymentAllocationModal({
  companyId,
  invoiceId,
  depositHt,
  onConfirm,
  onClose,
  onSaved,
}: {
  companyId: number;
  invoiceId: number;
  /** Valor HT do depósito que está sendo ligado agora (modo conciliação). */
  depositHt?: number;
  onConfirm?: (dossierIds: number[]) => void;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const t = useT();
  const q = trpc.customerInvoices.paymentAllocation.useQuery({ companyId, invoiceId });
  const save = trpc.customerInvoices.setPaymentAllocation.useMutation({
    onSuccess: () => {
      onSaved?.();
      onClose();
    },
  });
  const est = q.data;
  const modoLigar = onConfirm != null;
  const disponivel = (est?.receivedHt ?? 0) + (depositHt ?? 0);
  const [sel, setSel] = useState<Set<number> | null>(null);

  // Sugestão: mantém os já quitados e completa com os mais antigos que cabem.
  const sugerir = useMemo(
    () => () => {
      if (!est) return new Set<number>();
      const s = new Set(est.items.filter((i) => i.allocated).map((i) => i.dossierId));
      let soma = est.items.filter((i) => s.has(i.dossierId)).reduce((a, i) => a + i.dueHt, 0);
      for (const i of est.items) {
        if (s.has(i.dossierId)) continue;
        if (soma + i.dueHt <= disponivel + 0.01) {
          s.add(i.dossierId);
          soma += i.dueHt;
        }
      }
      return s;
    },
    [est, disponivel],
  );
  useEffect(() => {
    if (est && sel == null) {
      setSel(
        modoLigar
          ? sugerir()
          : new Set(est.items.filter((i) => i.allocated).map((i) => i.dossierId)),
      );
    }
  }, [est, sel, modoLigar, sugerir]);

  const marcados = sel ?? new Set<number>();
  const soma = (est?.items ?? [])
    .filter((i) => marcados.has(i.dossierId))
    .reduce((a, i) => a + i.dueHt, 0);
  const credito = disponivel - soma;
  const excede = soma > disponivel + 0.01;
  const m2 = (n: number) => formatMoney(n, { decimals: 2 });
  const toggle = (id: number) =>
    setSel((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="payment-allocation-modal"
      >
        <h2 className="text-base font-semibold">{t("paymentAllocation.title")}</h2>
        {est && (
          <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
            <span className="font-mono text-lime">{est.invoiceNumber}</span> ·{" "}
            {t("paymentAllocation.invoiceTotal")} {m2(est.netDueHt)} HT ·{" "}
            {t("paymentAllocation.alreadyReceived")} {m2(est.receivedHt)}
            {depositHt != null && (
              <>
                {" "}
                · {t("paymentAllocation.thisDeposit")}{" "}
                <span className="font-semibold">{m2(depositHt)}</span>
              </>
            )}
          </p>
        )}
        <p className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
          {t("paymentAllocation.hint")}
        </p>

        {q.isLoading && <p className="mt-3 text-sm">{t("paymentAllocation.loading")}</p>}
        {q.error && <p className="mt-3 text-sm text-red-400">{q.error.message}</p>}
        {est && (
          <div className="mt-3 min-h-0 flex-1 overflow-auto rounded-md border border-neutral-800">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-neutral-950 text-neutral-500">
                <tr>
                  <th className="w-8 px-2 py-1.5" />
                  <th className="px-2 py-1.5 text-left">{t("paymentAllocation.colCar")}</th>
                  <th className="px-2 py-1.5 text-left">{t("paymentAllocation.colOrder")}</th>
                  <th className="px-2 py-1.5 text-left">{t("paymentAllocation.colTech")}</th>
                  <th className="px-2 py-1.5 text-right">{t("paymentAllocation.colValue")}</th>
                  <th className="px-2 py-1.5 text-left">{t("paymentAllocation.colStatus")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {est.items.map((i) => {
                  const travado = i.allocated && i.locked;
                  return (
                    <tr
                      key={i.dossierId}
                      className={travado ? "opacity-70" : "cursor-pointer hover:bg-neutral-800/30"}
                      onClick={() => !travado && toggle(i.dossierId)}
                    >
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={marcados.has(i.dossierId)}
                          disabled={travado}
                          onChange={() => toggle(i.dossierId)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-mono font-semibold">{i.plate ?? i.vin ?? "—"}</div>
                        <div style={{ color: "var(--text-muted)" }}>
                          {i.vehicle ?? i.dossierCode}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 font-mono">{i.ordem ?? "—"}</td>
                      <td className="px-2 py-1.5">
                        {i.technicians.join(" + ") || "—"}
                        {i.technicianAmount > 0 && (
                          <div style={{ color: "var(--text-muted)" }}>
                            {t("paymentAllocation.techGets")} {m2(i.technicianAmount)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                        {m2(i.dueHt)}
                      </td>
                      <td className="px-2 py-1.5">
                        {i.allocated && (
                          <span className="badge b-ok">{t("paymentAllocation.stPaid")}</span>
                        )}{" "}
                        {i.locked && (
                          <span
                            className="badge b-mut"
                            title={
                              i.lockReason === "technician_paid"
                                ? t("paymentAllocation.lockTechPaid")
                                : t("paymentAllocation.lockProforma")
                            }
                          >
                            🔒
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {est && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span>
              {t("paymentAllocation.selected")}{" "}
              <span className="font-mono font-semibold">{m2(soma)}</span> ({marcados.size}{" "}
              {t("paymentAllocation.cars")})
            </span>
            <span>
              {t("paymentAllocation.available")}{" "}
              <span className="font-mono font-semibold">{m2(disponivel)}</span>
            </span>
            {excede ? (
              <span className="text-red-400">⚠ {t("paymentAllocation.exceeds")}</span>
            ) : (
              <span style={{ color: "var(--text-muted)" }}>
                {t("paymentAllocation.creditLeft")} <span className="font-mono">{m2(credito)}</span>
              </span>
            )}
          </div>
        )}
        {save.error && <p className="mt-2 text-xs text-red-400">{save.error.message}</p>}

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => setSel(sugerir())}
            disabled={!est}
            className="mr-auto rounded-md border border-neutral-800 px-3 py-1.5 text-xs hover:border-lime hover:text-lime"
          >
            {t("paymentAllocation.suggestBtn")}
          </button>
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-800 px-3 py-1.5 text-xs hover:border-neutral-600"
          >
            {t("paymentAllocation.cancelBtn")}
          </button>
          {modoLigar && est && (
            <button
              onClick={() =>
                onConfirm?.(est.items.filter((i) => i.allocated).map((i) => i.dossierId))
              }
              className="rounded-md border border-neutral-800 px-3 py-1.5 text-xs hover:border-neutral-600"
              title={t("paymentAllocation.linkNoCarsTitle")}
            >
              {t("paymentAllocation.linkNoCarsBtn")}
            </button>
          )}
          <button
            onClick={() => {
              const ids = [...marcados];
              if (onConfirm) onConfirm(ids);
              else save.mutate({ companyId, invoiceId, dossierIds: ids });
            }}
            disabled={!est || excede || save.isPending}
            className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime hover:bg-lime-bright disabled:opacity-40"
            data-testid="payment-allocation-confirm"
          >
            {modoLigar ? t("paymentAllocation.linkBtn") : t("paymentAllocation.saveBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
